import * as React from 'react';
import { useCallback, useEffect, 
         useMemo 
    }                                       from 'react';
import { useSelector, 
         useDispatch 
    }                                       from 'react-redux';
import { DataGridPro, 
         GridColDef,
         DataGridProProps,
         useGridApiRef,
         GridActionsCellItem,
         GridColumnGroupingModel,
         GridRenderEditCellParams,
         useGridApiContext
    }                                       from '@mui/x-data-grid-pro';
import { Box, 
         Tooltip, 
         Typography,
         Select,
         MenuItem,
         FormControl,
         Card,
         CardContent
       }                                    from '@mui/material';
import { useGlobalConfig }                  from 'context/GlobalContext';
import { ApplianceProfileAPI }              from 'apis/applianceProfileAPI';
import { selectCategories, 
         selectAppliances, 
         selectProfile, 
         setApplianceProfile,
         updateApplianceProfile,
         selectUnsavedRows,
         setUnsavedRowAction,
         discardUnsavedRowAction,
         discardAllUnsavedRowsAction
       }                                    from 'store/dataReducers/applianceProfileSlice';
import { selectCMSSelectedPilotDocId }      from 'store/dataReducers/cmsPilotSlice';
import { ApiErrorInfo }                     from 'types';
import { BglCloseOutlinedSmallButton, 
         BglPrimaryButton,  
         BglPrimaryOutlinedSmallButton 
       }                                    from 'components/button/BglButtons';
import { APIData }                          from 'types/cms/frontendTypes';
import VisibilityOffIcon                    from '@mui/icons-material/VisibilityOff';
import VisibilityIcon                       from '@mui/icons-material/Visibility';
import SaveIcon                             from '@mui/icons-material/Save';
import RestoreIcon                          from '@mui/icons-material/Restore';
import { SelectChangeEvent }                from '@mui/material/Select';
import { useTheme }                         from '@mui/material/styles';
import { getApplianceProfilePageStyles }    from './CMSApplianceProfilePageStyles';
import { BglLoading }                       from 'components/loading';

// Custom edit cell for defaultValue dropdown
const DefaultValueEditCell = (props: GridRenderEditCellParams) => {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();
  const categories = useSelector(selectCategories);
  const appliances = useSelector(selectAppliances);
  
  const [selectedValue, setSelectedValue] = React.useState<string>(value || '');
  
  // Find the appliance to get its category and fuel types
  const appliance = appliances.find(app => app.documentId === id);
  if (!appliance) return null;
  
  const categoryObj = categories.find(cat => cat.categoryId === appliance.categoryId);
  if (!categoryObj) return null;
  
  // Get dropdown options based on applianceCountFilter
  let dropdownOptions: string[] = [];
  if (categoryObj.applianceCountFilter) {
    // Show appliance count values from category object
    dropdownOptions = categoryObj.applianceCount as string[];
  } else {
    // Show fuel types + NONE
    const fuelTypes = appliance.fuelType as string[];
    dropdownOptions = [...fuelTypes, 'NONE'];
  }
  
  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value;
    setSelectedValue(newValue);
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };
  
  return (
    <FormControl fullWidth size="small">
      <Select
        value={selectedValue}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        sx={{
          border: 'none',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '22px',
          padding: '8px',
          borderRadius: '4px'
        }}
      >
        {dropdownOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};



interface TransformedApplianceData {
  id: string;
  path: string[];
  category: string;
  applianceCountFilter ?: boolean;
  subCategory: string;
  applianceLevelFilter ?: boolean;
  applianceId: string;
  name: string;
  fuelType: string;
  isCategory?: boolean;
  isSubCategory?: boolean;
  disabled ?: boolean;
  defaultValue ?: boolean;
}

const columnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'Category.Subcategory.Appliance',
    description: '',
    children: [
      {field: '__tree_data_group__'}, 
      {field: 'applianceId'}, 
      {field: 'fuelType'}, 
      {field: 'name'},
      {field: 'defaultValue'},
      {field: 'disabled'}
    ],
  },
  {
    groupId: 'Exclude',
    description: '',
    children: [
      {field: 'applianceCountFilter'},
      {field: 'applianceLevelFilter'},
    ],
  }
]

export default function CMSApplianceProfilePage() {

  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const styles = getApplianceProfilePageStyles(theme);
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();

  const [isLoading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<ApiErrorInfo | undefined>(undefined);
  const [isSaving, setIsSaving] = React.useState(false);
  
  const categories = useSelector(selectCategories);
  const appliances = useSelector(selectAppliances);
  const profile = useSelector(selectProfile)!;
  const utilityDocId = useSelector(selectCMSSelectedPilotDocId);
  const unsavedRows = useSelector(selectUnsavedRows);

  // Check if user has permission to update appliance profile attributes
  const canUpdate = rc.permissionsManager.canUtilityApplianceProfileUpdate();

  const getApplianceProfile = async () => {

    setLoading(true);

    const resp = await ApplianceProfileAPI.getApplianceProfile(rc, utilityDocId);

    if (resp.error) {
      setError(resp.error);
    } else {
      dispatch(setApplianceProfile(resp.data));
      setError(undefined);
    }
    setLoading(false);
  }

  const saveApplianceChanges = useCallback(async (applianceId: string, changes: any) => {
    setIsSaving(true);
    
    const updatedProfile = {
      defaultValue: {
        [applianceId]: changes.defaultValue
      },
      disabled: changes.disabled ? [applianceId] : undefined
    };
    
    const resp = await ApplianceProfileAPI.updateApplianceProfile(rc, utilityDocId, updatedProfile);
    
    if (resp.error) {
      setError(resp.error);
    } else {
      dispatch(updateApplianceProfile(resp.data));
      // Remove from unsaved rows
      dispatch(discardUnsavedRowAction(applianceId));
    }
    
    setIsSaving(false);
  }, [rc, utilityDocId, dispatch]);

  // Store data in Redux when API response is received
  useEffect(() => {

    if (!profile) {
      getApplianceProfile();
    } else {
      setLoading(false);
    }
    
  }, [profile]);

  // Transform data into tree structure for DataGridPro
  const transformedData = useMemo(() => {
    if (!appliances || appliances.length === 0 || !profile) return [];

    const result: TransformedApplianceData[] = [];

    // Group appliances by category and subcategory
    const categoryMap = new Map<string, Map<string, APIData<'api::appliance.appliance'>[]>>();

    appliances.forEach((appliance) => {
      
      const categoryObj = categories.find(cat => cat.categoryId === appliance.categoryId)!;
      const categoryName = categoryObj.name!;
      
      const subCategoryObjs = (categoryObj.subCategory as any[]).filter((subCat : any) => subCat.id === appliance.subCategoryId);
      const subCategoryObj = subCategoryObjs[0];
      const subCategoryName = subCategoryObj.name!;

      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, new Map());
      }

      const subCategoryMap = categoryMap.get(categoryName)!;
      if (!subCategoryMap.has(subCategoryName)) {
        subCategoryMap.set(subCategoryName, []);
      }

      subCategoryMap.get(subCategoryName)!.push(appliance);
    });

    // Sort categories alphabetically
    const sortedCategories = Array.from(categoryMap.keys()).sort();

    // Build tree structure with proper sorting
    sortedCategories.forEach((categoryName) => {
      const subCategoryMap = categoryMap.get(categoryName)!;
      const categoryObj = categories.find(cat => cat.name === categoryName)!;
      
      // Add category row
      result.push({
        id: `category-${categoryName}`,
        path: [categoryName],
        category: categoryName,
        subCategory: '',
        applianceCountFilter: categoryObj.applianceCountFilter,
        applianceId: '',
        name: '',
        fuelType: '',
        isCategory: true
      });

      // Sort subcategories alphabetically
      const sortedSubCategories = Array.from(subCategoryMap.keys()).sort();

      sortedSubCategories.forEach((subCategoryName) => {
        const appliancesInSubCategory = subCategoryMap.get(subCategoryName)!;
        const subCategoryObj = (categoryObj.subCategory as any[]).find((subCat: any) => subCat.name === subCategoryName)!;
        
        // Add subcategory row
        result.push({
          id: `subcategory-${categoryName}-${subCategoryName}`,
          path: [categoryName, subCategoryName],
          category: '',
          subCategory: subCategoryName,
          applianceLevelFilter: subCategoryObj.applianceLevelFilter || false,
          applianceId: '',
          name: '',
          fuelType: '',
          isSubCategory: true
        });

        // Sort appliances alphabetically by name
        const sortedAppliances = appliancesInSubCategory.sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB);
        });

        // Add appliance rows
        sortedAppliances.forEach((appliance) => {

          const defaultValue = (profile.defaultValue as any)[appliance.applianceId]
          const disabled = (profile.disabled as any).includes(appliance.applianceId)

          result.push({
            id: appliance.documentId,
            path: [categoryName, subCategoryName, appliance.name],
            category: '',
            subCategory: '',
            applianceId: appliance.applianceId.toString(),
            name: appliance.name,
            defaultValue: defaultValue,
            disabled: disabled,
            fuelType: Array.isArray(appliance.fuelType) ? appliance.fuelType.join(', ') : ''
          });
        });
      });
    });

    return result;
  }, [appliances, categories, profile]);

  const processRowUpdate = useCallback<NonNullable<DataGridProProps['processRowUpdate']>>(
    (newRow: any, oldRow: any) => {
      // Only track changes for appliance rows (not category or subcategory)
      if (newRow.isCategory || newRow.isSubCategory) {
        return newRow;
      }
      
      // Find the original row from transformedData (source of truth)
      const originalRow = transformedData.find(row => row.applianceId === newRow.applianceId);
      if (!originalRow) {
        return newRow;
      }
      
      // Calculate diff between new row and original row
      const changes: any = {};
      let hasChanges = false;
      
      // Check for defaultValue changes
      if (newRow.defaultValue !== originalRow.defaultValue) {
        changes.defaultValue = newRow.defaultValue;
        hasChanges = true;
      }
      
      // Check for disabled changes
      if (newRow.disabled !== originalRow.disabled) {
        changes.disabled = newRow.disabled;
        hasChanges = true;
      }
      
      if (hasChanges) {
        dispatch(setUnsavedRowAction({ id: newRow.applianceId, diff: changes }));
      } else {
        // Remove from unsaved rows if no changes
        dispatch(discardUnsavedRowAction(newRow.applianceId));
      }
      
      return newRow;
    },
    [dispatch, transformedData]
  );

  const onProcessRowUpdateError = React.useCallback<NonNullable<DataGridProProps['onProcessRowUpdateError']>>(
    (error: any) => {
      console.error(error);
    },
    []
  );

  const discardApplianceChanges = useCallback((applianceId: string) => {
    // Remove from unsaved rows - this will trigger a re-render with the original data
    dispatch(discardUnsavedRowAction(applianceId));
  }, [dispatch]);

  const toggleApplianceDisabled = useCallback((applianceId: string, currentDisabled: boolean) => {
    const changes = {
      disabled: !currentDisabled
    };
    
    dispatch(setUnsavedRowAction({ id: applianceId, diff: changes }));
  }, [dispatch]);

  const discardAllUnsavedChanges = useCallback(() => {
    // Discard all unsaved changes
    dispatch(discardAllUnsavedRowsAction());
  }, [dispatch]);

  const saveAllUnsavedChanges = useCallback(async () => {
    setIsSaving(true);
    
    try {
      // Save all unsaved changes
      const unsavedEntries = Object.entries(unsavedRows);

      // Create a new profile with the unsaved changes
      const updatedProfile = {
        defaultValue: {} as Record<string, string>,
        disabled: [] as string[]
      };

      for (const [applianceId, changes] of unsavedEntries) {
        if (changes.defaultValue) {
          updatedProfile.defaultValue[applianceId] = changes.defaultValue;
        }
        if (changes.disabled) {
          updatedProfile.disabled.push(applianceId);
        }
      }

      const resp = await ApplianceProfileAPI.updateApplianceProfile(rc, utilityDocId, updatedProfile);

      if (resp.error) {
        setError(resp.error);
      } else {
        dispatch(updateApplianceProfile(resp.data));
        dispatch(discardAllUnsavedRowsAction());
      }

    } catch (error) {
      console.error('Error saving all changes:', error);
    } finally {
      setIsSaving(false);
    }
  }, [unsavedRows]);

  // Update unsaved rows when cells are edited
  useEffect(() => {
    // Get all appliance rows from transformedData
    const applianceRows = transformedData.filter(row => row.applianceId);
    
    applianceRows.forEach((originalRow) => {
      const applianceId = originalRow.applianceId;
      const unsavedRow = unsavedRows[applianceId];
      
      if (unsavedRow) {
        // Apply unsaved changes
        apiRef.current?.updateRows([{...originalRow, ...unsavedRow}]);
      } else {
        // Reset to original values (no unsaved changes)
        apiRef.current?.updateRows([originalRow]);
      }
    });
  }, [apiRef, transformedData, unsavedRows]);

  // Expand all tree nodes when data is loaded and keep them expanded
  useEffect(() => {
    if (transformedData.length > 0 && !isLoading) {
      const categoryAndSubCategoryIds = transformedData
        .filter(row => row.isCategory || row.isSubCategory)
        .map(row => row.id);
      
      // Expand all category and subcategory nodes
      categoryAndSubCategoryIds.forEach(id => {
        apiRef.current?.setRowChildrenExpansion(id, true);
      });
    }
  }, [transformedData, isLoading, apiRef, unsavedRows]);

  const getTreeDataPath: DataGridProProps['getTreeDataPath'] = (row) => row.path;

  const getRowClassName: DataGridProProps['getRowClassName'] = (params) => {
    const row = transformedData.find(r => r.id === params.id);
    if (row?.isCategory) {
      return 'category-row';
    } else if (row?.isSubCategory) {
      return 'subcategory-row';
    } else {
      // Check if appliance row has unsaved changes
      const applianceId = row?.applianceId;
      if (applianceId && unsavedRows[applianceId]) {
        return 'appliance-row row--edited';
      }
      // Check if appliance is disabled
      if (row?.disabled) {
        return 'appliance-row row--disabled';
      }
      return 'appliance-row';
    }
  };

  const groupingColDef: DataGridProProps['groupingColDef'] = {
    headerName: 'Appliance',
    hideDescendantCount: true,
    width: 300,
  }

  const columns: GridColDef[] = [
    {
      field: 'applianceId',
      headerName: 'Appliance Id',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          display: 'flex', 
          height: '100%', 
          alignItems: 'center',
          color: 'inherit',
        }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'fuelType',
      headerName: 'Fuel Types',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          display: 'flex', 
          height: '100%', 
          alignItems: 'center',
          color: 'inherit',
        }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'defaultValue',
      headerName: 'Default Profile',
      width: 150,
      editable: canUpdate,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          display: 'flex', 
          height: '100%', 
          alignItems: 'center',
          color: 'inherit',
        }}>
          {params.value || ''}
        </Typography>
      ),
      renderEditCell: (params: GridRenderEditCellParams) => (
        <DefaultValueEditCell {...params} />
      )
    },
    {
      field: 'disabled',
      headerName: 'Disabled',
      width: 120,
      type: 'boolean',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          display: 'flex', 
          height: '100%', 
          alignItems: 'center',
          color: 'inherit',
        }}>
          {params.value !== undefined ? (params.value ? 'Yes' : 'No') : ''}
        </Typography>
      )
    },
    // {
    //   field: 'trueDisagg',
    //   headerName: 'True Disagg',
    //   width: 120,
    //   type: 'boolean',
    //   renderCell: (params) => (
    //     <Typography variant="body2">
    //       {params.value !== undefined ? (params.value ? 'Yes' : 'No') : ''}
    //     </Typography>
    //   )
    // },
    {
      field: 'applianceCountFilter',
      headerName: 'by Appliance Count',
      width: 200,
      type: 'boolean',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          display: 'flex', 
          height: '100%', 
          alignItems: 'center',
          color: 'inherit',
        }}>
          { params.value !== undefined ? (params.value ? 'Yes' : 'No') : ''}
        </Typography>
      )
    },
    {
      field: 'applianceLevelFilter',
      headerName: 'on Subcategory Group',
      width: 200,
      type: 'boolean',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          display: 'flex', 
          height: '100%', 
          alignItems: 'center',
          color: 'inherit',
        }}>
          {params.value !== undefined ? (params.value ? 'No' : 'Yes') : ''}
        </Typography>
      )
    },
    {
      field: 'actions',
      type: 'actions',
      minWidth: 240,
      flex: 1,
      headerName: 'Actions',
      align: 'left',
      headerAlign: 'left',
      getActions : ({id, row}) => {
        // Only show actions for appliance rows
        if (row.isCategory || row.isSubCategory) {
          return [];
        }
        
        const applianceId = row.applianceId;
        const hasUnsavedChanges = unsavedRows[applianceId];
        
        return [
          <Tooltip title={row.disabled ? "Enable appliance" : "Disable appliance"}>
            <span>
              <GridActionsCellItem
                icon={row.disabled ? <VisibilityIcon /> : <VisibilityOffIcon />}
                label={row.disabled ? "Enable appliance" : "Disable appliance"}
                disabled={!canUpdate}
                onClick={() => toggleApplianceDisabled(applianceId, row.disabled!)}
              />
            </span>
          </Tooltip>,
          <Tooltip title="Discard changes">
            <span>
              <GridActionsCellItem
                icon={<RestoreIcon />}
                label="Discard changes"
                disabled={!hasUnsavedChanges || !canUpdate}
                onClick={() => discardApplianceChanges(applianceId)}
              />
            </span>
          </Tooltip>,
          <Tooltip title="Save changes">
            <span>
              <GridActionsCellItem
                icon={<SaveIcon />}
                label="Save changes"
                disabled={!hasUnsavedChanges || !canUpdate}
                onClick={() => saveApplianceChanges(applianceId, hasUnsavedChanges)}
              />
            </span>
          </Tooltip>
        ]
      }
    }
  ];

  return (
    <Box sx={styles.applianceProfileContainer}>

    {
      isLoading ? 
      <Box sx={styles.loadingContainer}>
        <BglLoading />
      </Box> 
      
      : error ? 
      <Box sx={styles.errorContainer}>
        <Typography variant="h6" sx={{ 
          color: theme.palette.text.primary
        }}>{error.message}</Typography>
        <BglPrimaryButton
          onClick={() => getApplianceProfile()}>
          Retry
        </BglPrimaryButton>
      </Box>

      : 
      <>
        {/* Guidelines */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body1" sx={{ 
                fontWeight: 'bold',
                color: theme.palette.text.secondary
              }}>Guidelines</Typography>
              <Typography variant="body2" sx={{ 
                color: theme.palette.text.secondary
              }}>Here we configure the Appliance Profile for the Project indicating which Appliances are considered as part of each category & subcategory, and for which appliances we would take into account the definition of - </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ 
                  color: theme.palette.text.secondary
                }}><b>Default Profile </b>: Value to consider when no User Survey response is available for the appliance</Typography>
                <Typography variant="body2" sx={{ 
                  color: theme.palette.text.secondary
                }}><b>Disabled </b>: Disable the appliance from being considered for Exclusion (Recos)</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ mb: 1, display: 'flex', gap: 2 }}>
          <BglCloseOutlinedSmallButton
            disabled={isSaving || !canUpdate || Object.keys(unsavedRows).length === 0}
            onClick={discardAllUnsavedChanges}
            startIcon={<RestoreIcon />}>
            {`Discard all (${Object.keys(unsavedRows).length})`}
          </BglCloseOutlinedSmallButton>
          <BglPrimaryOutlinedSmallButton
            disabled={isSaving || !canUpdate || Object.keys(unsavedRows).length === 0}
            onClick={saveAllUnsavedChanges}
            startIcon={<SaveIcon />}>
            {`Save all (${Object.keys(unsavedRows).length})`}
          </BglPrimaryOutlinedSmallButton>
        </Box>

      <DataGridPro
        apiRef={apiRef}
        treeData
        getTreeDataPath={getTreeDataPath}
        getRowClassName={getRowClassName}
        groupingColDef={groupingColDef}
        columnGroupingModel={columnGroupingModel}
        rows={transformedData}
        columns={columns}
        loading={isLoading || isSaving}
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={onProcessRowUpdateError}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
        sx={styles.dataGrid}
      />
      <br/>
      </>
      }

    </Box>
  );
}
